import React, { useEffect, useState, useRef } from 'react';
import {
  getFactories, createFactory, updateFactory,
  getFactoryCategories, createFactoryCategory, updateFactoryCategory, deleteFactoryCategory,
  reorderFactoriesApi, reorderFactoryCategoriesApi
} from '../services/api';
import toast from 'react-hot-toast';

const emptyForm = { name: '', address: '', category_id: '' };

export default function Factories() {
  const [factories, setFactories] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  // Factory modal
  const [showModal, setShowModal] = useState(false);
  const [editFactory, setEditFactory] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Category modal
  const [showCatModal, setShowCatModal] = useState(false);
  const [catEditId, setCatEditId] = useState(null);
  const [catEditName, setCatEditName] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [savingCat, setSavingCat] = useState(false);

  // Modal drag state (category modal)
  const modalDragItem = useRef(null);
  const modalDropDone = useRef(false);
  const [modalDraggingId, setModalDraggingId] = useState(null);

  // Main page drag state
  const dragType = useRef(null); // 'factory' | 'cat'
  const factDragItem = useRef(null);
  const factDragCatId = useRef(null);
  const factDropDone = useRef(false);
  const [draggingFactId, setDraggingFactId] = useState(null);

  const catDragItem = useRef(null);
  const catDropDone = useRef(false);
  const [draggingCatId, setDraggingCatId] = useState(null);
  const [catDropIndicator, setCatDropIndicator] = useState(null); // {catId, position}

  const load = () => {
    setLoading(true);
    Promise.all([getFactories(), getFactoryCategories()])
      .then(([fRes, cRes]) => {
        setFactories(fRes.data);
        setCats(cRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // ============================================================
  // FACTORY MODAL
  // ============================================================
  const openCreate = () => {
    setEditFactory(null);
    setForm(emptyForm);
    setShowModal(true);
  };
  const openEdit = (f) => {
    setEditFactory(f);
    setForm({ name: f.name, address: f.address || '', category_id: f.category_id ? String(f.category_id) : '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Το όνομα είναι υποχρεωτικό');
    setSaving(true);
    try {
      const data = {
        name: form.name,
        address: form.address,
        category_id: form.category_id ? parseInt(form.category_id) : null,
      };
      if (editFactory) {
        await updateFactory(editFactory.id, data);
        toast.success('Το εργοστάσιο ενημερώθηκε');
      } else {
        await createFactory(data);
        toast.success('Το εργοστάσιο δημιουργήθηκε');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (f) => {
    try {
      await updateFactory(f.id, { is_active: !f.is_active });
      toast.success(f.is_active ? 'Απενεργοποιήθηκε' : 'Ενεργοποιήθηκε');
      load();
    } catch { toast.error('Σφάλμα'); }
  };

  // ============================================================
  // CATEGORY MODAL
  // ============================================================
  const openCatModal = () => {
    setShowCatModal(true);
    setCatEditId(null);
    setCatEditName('');
    setNewCatName('');
  };

  const handleCreateCat = async () => {
    if (!newCatName.trim()) return;
    setSavingCat(true);
    try {
      await createFactoryCategory({ name: newCatName.trim() });
      toast.success('Η κατηγορία δημιουργήθηκε');
      setNewCatName('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSavingCat(false);
    }
  };

  const startEditCat = (cat) => {
    setCatEditId(cat.id);
    setCatEditName(cat.name);
  };

  const saveCatEdit = async () => {
    if (!catEditName.trim()) return;
    setSavingCat(true);
    try {
      await updateFactoryCategory(catEditId, { name: catEditName.trim() });
      toast.success('Η κατηγορία ενημερώθηκε');
      setCatEditId(null);
      setCatEditName('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSavingCat(false);
    }
  };

  const deleteCat = async (cat) => {
    const factoriesInCat = factories.filter(f => f.category_id === cat.id);
    if (factoriesInCat.length > 0) {
      if (!window.confirm(
        `Η κατηγορία "${cat.name}" έχει ${factoriesInCat.length} εργοστάσια. Να αφαιρεθούν από την κατηγορία (θα γίνουν "Χωρίς Κατηγορία") και να διαγραφεί η κατηγορία;`
      )) return;
      try {
        await deleteFactoryCategory(cat.id, true);
        toast.success('Η κατηγορία διαγράφηκε');
        load();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Σφάλμα');
      }
      return;
    }
    if (!window.confirm(`Διαγραφή κατηγορίας "${cat.name}";`)) return;
    try {
      await deleteFactoryCategory(cat.id);
      toast.success('Η κατηγορία διαγράφηκε');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    }
  };

  // ============================================================
  // MODAL CATEGORY DRAG & DROP (reorder inside the category modal)
  // ============================================================
  const onModalDragStart = (e, cat) => {
    modalDragItem.current = cat;
    setModalDraggingId(cat.id);
    modalDropDone.current = false;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };
  const onModalDragOver = (e, targetCat) => {
    e.preventDefault();
    if (!modalDragItem.current || modalDragItem.current.id === targetCat.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const position = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    const currentOrder = [...cats];
    const fromIdx = currentOrder.findIndex(c => c.id === modalDragItem.current.id);
    if (fromIdx === -1) return;
    const draggedCat = currentOrder.splice(fromIdx, 1)[0];
    let toIdx = currentOrder.findIndex(c => c.id === targetCat.id);
    if (toIdx === -1) return;
    if (position === 'below') toIdx += 1;
    currentOrder.splice(toIdx, 0, draggedCat);
    setCats(currentOrder);
  };
  const onModalDrop = (e) => {
    e.preventDefault();
    modalDropDone.current = true;
    reorderFactoryCategoriesApi(cats.map(c => c.id)).catch(() => {
      toast.error('Σφάλμα αποθήκευσης σειράς');
      load();
    });
  };
  const onModalDragEnd = () => {
    setModalDraggingId(null);
    modalDragItem.current = null;
    if (!modalDropDone.current) load();
  };

  // ============================================================
  // MAIN PAGE DRAG & DROP
  // ============================================================

  // --- Category drag (reorder categories)
  const onCatDragStart = (e, cat) => {
    dragType.current = 'cat';
    catDragItem.current = cat;
    setDraggingCatId(cat.id);
    catDropDone.current = false;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };
  const onCatDragOver = (e, targetCat) => {
    if (dragType.current !== 'cat') return;
    e.preventDefault();
    if (!catDragItem.current || catDragItem.current.id === targetCat.id) {
      setCatDropIndicator(null); return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const position = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    setCatDropIndicator({ catId: targetCat.id, position });
  };
  const onCatDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setCatDropIndicator(null);
  };
  const onCatDrop = (e, targetCat) => {
    if (dragType.current !== 'cat') return;
    e.preventDefault(); e.stopPropagation(); catDropDone.current = true;
    if (!catDragItem.current || catDragItem.current.id === targetCat.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const position = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    const currentOrder = [...cats];
    const fromIdx = currentOrder.findIndex(c => c.id === catDragItem.current.id);
    if (fromIdx === -1) return;
    const draggedCat = currentOrder.splice(fromIdx, 1)[0];
    let toIdx = currentOrder.findIndex(c => c.id === targetCat.id);
    if (toIdx === -1) return;
    if (position === 'below') toIdx += 1;
    currentOrder.splice(toIdx, 0, draggedCat);
    setCats(currentOrder);
    reorderFactoryCategoriesApi(currentOrder.map(c => c.id)).catch(() => {
      toast.error('Σφάλμα αποθήκευσης σειράς'); load();
    });
  };
  const onCatDragEnd = () => {
    setDraggingCatId(null);
    setCatDropIndicator(null);
    catDragItem.current = null;
    catDropDone.current = false;
    dragType.current = null;
  };

  // --- Factory drag (reorder within category or move to another category)
  const onFactDragStart = (e, f, catId) => {
    dragType.current = 'factory';
    factDragItem.current = f;
    factDragCatId.current = catId;
    setDraggingFactId(f.id);
    factDropDone.current = false;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };
  const onFactDragOver = (e) => {
    if (dragType.current !== 'factory') return;
    e.preventDefault();
  };
  // When dropping inside a category card body/header, move factory into that category
  const onFactDropIntoCategory = async (e, targetCatId) => {
    if (dragType.current !== 'factory' || !factDragItem.current) return;
    e.preventDefault(); e.stopPropagation();
    factDropDone.current = true;
    const f = factDragItem.current;
    const srcCat = factDragCatId.current;
    const targetCat = targetCatId === undefined ? null : targetCatId;
    // No-op if same category
    if ((srcCat ?? null) === (targetCat ?? null)) return;
    try {
      await updateFactory(f.id, { category_id: targetCat });
      toast.success('Μετακινήθηκε');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
      load();
    }
  };
  // Drop onto another factory row: reorder within same category (or move + reorder)
  const onFactDropOnFactory = async (e, targetF, targetCatId) => {
    if (dragType.current !== 'factory' || !factDragItem.current) return;
    e.preventDefault(); e.stopPropagation();
    factDropDone.current = true;
    const f = factDragItem.current;
    if (f.id === targetF.id) return;

    const targetCat = targetCatId === undefined ? null : targetCatId;
    // If moving between categories, update category first
    if ((f.category_id ?? null) !== (targetCat ?? null)) {
      try { await updateFactory(f.id, { category_id: targetCat }); }
      catch (err) { toast.error('Σφάλμα'); load(); return; }
    }
    const listInCat = factories
      .filter(x => (x.category_id ?? null) === (targetCat ?? null))
      .filter(x => showInactive || x.is_active)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    let withoutDragged = listInCat.filter(x => x.id !== f.id);
    const targetIdx = withoutDragged.findIndex(x => x.id === targetF.id);
    if (targetIdx === -1) { load(); return; }
    withoutDragged.splice(targetIdx, 0, { ...f, category_id: targetCat });
    try {
      await reorderFactoriesApi(withoutDragged.map(x => x.id));
      load();
    } catch (err) {
      toast.error('Σφάλμα αποθήκευσης σειράς');
      load();
    }
  };
  const onFactDragEnd = () => {
    setDraggingFactId(null);
    factDragItem.current = null;
    factDragCatId.current = null;
    factDropDone.current = false;
    dragType.current = null;
  };

  // ============================================================
  // HELPERS
  // ============================================================
  const getFactoriesByCat = (catId) =>
    factories
      .filter(f => (f.category_id ?? null) === (catId ?? null))
      .filter(f => showInactive || f.is_active)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const uncategorizedFactories = getFactoriesByCat(null);

  const dragHandleStyle = {
    cursor: 'grab', padding: '0 8px', color: 'var(--gray-300)',
    fontSize: 18, userSelect: 'none', display: 'flex', alignItems: 'center',
  };
  const dropLineStyle = {
    height: 4, background: '#3b82f6', borderRadius: 2, margin: '4px 0',
  };

  // ============================================================
  // RENDER FACTORY ROW (inside a category)
  // ============================================================
  const renderFactoryRow = (f, catId) => {
    const isDragging = draggingFactId === f.id;
    return (
      <div
        key={f.id}
        draggable
        onDragStart={(e) => onFactDragStart(e, f, catId)}
        onDragEnd={onFactDragEnd}
        onDragOver={onFactDragOver}
        onDrop={(e) => onFactDropOnFactory(e, f, catId)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 16px',
          borderTop: '1px solid var(--gray-100)',
          background: isDragging ? '#dcfce7' : undefined,
          opacity: isDragging ? 0.5 : 1,
          transition: 'opacity 0.15s ease',
        }}
      >
        <span style={{ ...dragHandleStyle, color: isDragging ? '#22c55e' : 'var(--gray-300)' }}>⠿</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-display)' }}>{f.name}</div>
          {f.address && <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>📍 {f.address}</div>}
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>
            ID: {f.id} · {new Date(f.created_at).toLocaleDateString('el-GR')}
          </div>
        </div>
        <span className={`badge ${f.is_active ? 'badge-green' : 'badge-gray'}`}>
          {f.is_active ? 'Ενεργό' : 'Ανενεργό'}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(f)}>✏️ Επεξεργασία</button>
          <button
            className={`btn btn-sm ${f.is_active ? 'btn-danger' : 'btn-success'}`}
            onClick={() => handleToggle(f)}
          >
            {f.is_active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
          </button>
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER CATEGORY CARD
  // ============================================================
  const renderCategoryCard = (cat) => {
    const catFacts = getFactoriesByCat(cat.id);
    const isCatDragging = draggingCatId === cat.id;
    const showAbove = catDropIndicator?.catId === cat.id && catDropIndicator.position === 'above';
    const showBelow = catDropIndicator?.catId === cat.id && catDropIndicator.position === 'below';

    return (
      <div key={cat.id}>
        {showAbove && <div style={dropLineStyle} />}
        <div
          className="card"
          style={{
            marginBottom: 16,
            border: isCatDragging ? '2px solid #22c55e' : undefined,
            opacity: isCatDragging ? 0.4 : 1,
            transition: 'opacity 0.15s ease',
          }}
          onDragOver={(e) => {
            if (dragType.current === 'cat' && draggingCatId) onCatDragOver(e, cat);
            if (dragType.current === 'factory') e.preventDefault();
          }}
          onDragLeave={dragType.current === 'cat' ? onCatDragLeave : undefined}
          onDrop={(e) => {
            if (dragType.current === 'cat' && catDragItem.current) onCatDrop(e, cat);
            if (dragType.current === 'factory') onFactDropIntoCategory(e, cat.id);
          }}
        >
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <span
              draggable
              onDragStart={(e) => onCatDragStart(e, cat)}
              onDragEnd={onCatDragEnd}
              style={{ ...dragHandleStyle, color: isCatDragging ? '#22c55e' : 'var(--gray-300)' }}
              title="Σύρετε για αλλαγή σειράς κατηγορίας"
            >⠿</span>
            <span className="card-title">{cat.name}</span>
            <span className="badge badge-gray" style={{ marginLeft: 'auto' }}>
              {catFacts.length} εργοστάσια
            </span>
          </div>
          {catFacts.length === 0 ? (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13, fontStyle: 'italic' }}>
              Κενή κατηγορία — σύρετε εργοστάσια εδώ
            </div>
          ) : (
            <div>
              {catFacts.map(f => renderFactoryRow(f, cat.id))}
            </div>
          )}
        </div>
        {showBelow && <div style={dropLineStyle} />}
      </div>
    );
  };

  return (
    <>
      <div className="page-header" style={{ position: 'sticky', top: 0, zIndex: 20, background: 'var(--gray-50)', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--gray-200)' }}>
        <div className="flex-between">
          <div>
            <h2>Εργοστάσια</h2>
            <p>Διαχείριση εγκαταστάσεων παραγωγής — οργανωμένα σε κατηγορίες</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Ανενεργά
            </label>
            <button className="btn btn-secondary" onClick={openCatModal}>+ Νέα Κατηγορία</button>
            <button className="btn btn-primary" onClick={openCreate}>+ Νέο Εργοστάσιο</button>
          </div>
        </div>
      </div>
      <div className="page-body">
        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : cats.length === 0 && factories.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">🏭</div>
              <h3>Δεν υπάρχουν εργοστάσια</h3>
              <p>Δημιουργήστε πρώτα μια κατηγορία και μετά προσθέστε εργοστάσια</p>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-secondary" onClick={openCatModal}>+ Νέα Κατηγορία</button>
                <button className="btn btn-primary" onClick={openCreate}>+ Νέο Εργοστάσιο</button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {cats.map(cat => renderCategoryCard(cat))}
            {uncategorizedFactories.length > 0 && (
              <div
                className="card"
                style={{ marginBottom: 16 }}
                onDragOver={(e) => { if (dragType.current === 'factory') e.preventDefault(); }}
                onDrop={(e) => { if (dragType.current === 'factory') onFactDropIntoCategory(e, null); }}
              >
                <div className="card-header">
                  <span className="card-title">Χωρίς Κατηγορία</span>
                  <span className="badge badge-gray" style={{ marginLeft: 'auto' }}>
                    {uncategorizedFactories.length} εργοστάσια
                  </span>
                </div>
                <div>
                  {uncategorizedFactories.map(f => renderFactoryRow(f, null))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* FACTORY MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editFactory ? 'Επεξεργασία Εργοστασίου' : 'Νέο Εργοστάσιο'}</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Όνομα Εργοστασίου *</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="π.χ. Αρτοποιείο Κεντρικό" />
              </div>
              <div className="form-group">
                <label className="form-label">Διεύθυνση</label>
                <input className="form-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="π.χ. Αθήνα, Οδός 123" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Κατηγορία</label>
                <select className="form-input" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                  <option value="">— Χωρίς κατηγορία —</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Ακύρωση</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : (editFactory ? 'Αποθήκευση' : 'Δημιουργία')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CATEGORY MANAGEMENT MODAL */}
      {showCatModal && (
        <div className="modal-overlay" onClick={() => setShowCatModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <span className="modal-title">Διαχείριση Κατηγοριών</span>
              <button className="modal-close" onClick={() => setShowCatModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              {cats.length === 0 ? (
                <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--gray-400)' }}>Δεν υπάρχουν κατηγορίες.</div>
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {cats.map(cat => {
                    const isEditing = catEditId === cat.id;
                    const isDragging = modalDraggingId === cat.id;
                    const factCount = factories.filter(f => f.category_id === cat.id).length;
                    return (
                      <div
                        key={cat.id}
                        draggable={!isEditing}
                        onDragStart={(e) => onModalDragStart(e, cat)}
                        onDragOver={(e) => onModalDragOver(e, cat)}
                        onDrop={onModalDrop}
                        onDragEnd={onModalDragEnd}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '12px 20px',
                          borderBottom: '1px solid var(--gray-100)',
                          background: isDragging ? '#dcfce7' : undefined,
                          opacity: isDragging ? 0.5 : 1,
                        }}
                      >
                        {!isEditing && (
                          <span style={{ ...dragHandleStyle, color: isDragging ? '#22c55e' : 'var(--gray-300)' }}>⠿</span>
                        )}
                        {isEditing ? (
                          <>
                            <input
                              className="form-input"
                              value={catEditName}
                              onChange={e => setCatEditName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveCatEdit(); if (e.key === 'Escape') setCatEditId(null); }}
                              autoFocus
                              style={{ flex: 1 }}
                            />
                            <button className="btn btn-primary btn-sm" onClick={saveCatEdit} disabled={savingCat}>Αποθήκευση</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setCatEditId(null)}>Ακύρωση</button>
                          </>
                        ) : (
                          <>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>{cat.name}</div>
                              <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{factCount} εργοστάσια</div>
                            </div>
                            <button className="btn btn-secondary btn-sm" onClick={() => startEditCat(cat)}>✏️</button>
                            <button className="btn btn-danger btn-sm" onClick={() => deleteCat(cat)}>🗑️</button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-input"
                  placeholder="Όνομα νέας κατηγορίας"
                  value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateCat(); }}
                  style={{ flex: 1 }}
                />
                <button className="btn btn-primary" onClick={handleCreateCat} disabled={savingCat || !newCatName.trim()}>
                  + Προσθήκη
                </button>
              </div>
              <button className="btn btn-secondary" onClick={() => setShowCatModal(false)}>Κλείσιμο</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
